import { useRef } from "react";
import { Link } from "react-router-dom";
import WhoAreWe from "../WhoAreWe";
import CTA from "./CTA";


const AiDevProcess = () => {
  const processRef = useRef(null);

  const processes = [
    { 
      title: "Discover & Strategize", 
      description: "Deep-dive analysis of business challenges and strategic AI opportunity mapping.",
      iconPath: "/images/strategy.png",
    },
    { 
      title: "Co-Develop & Co-Create", 
      description: "Engineering and training high-performance models using state-of-the-art neural networks.",
      iconPath: "/images/build.png",
    },
    { 
      title: "Deploy & Scale", 
      description: "Production-grade deployment with proactive monitoring and continuous optimization.",
      iconPath: "/images/deploy.png",
    }
  ];

  return (
    <section ref={processRef} className="py-32 bg-white relative overflow-hidden selection:bg-[#952877]/10 selection:text-[#952877]">
      {/* Liquid Ambient Glows */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#952877]/5 blur-[130px] rounded-full pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#592aa7]/5 blur-[130px] rounded-full pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header Section */}
        <div className="text-center mb-24">
          {/* <div className="inline-block px-4 py-1.5 mb-6 rounded-full border border-[#952877]/20 bg-[#952877]/5">
             <span className="text-[10px] font-black tracking-[0.2em] uppercase text-[#952877]">Execution Framework</span>
          </div> */}
          <h2 className="text-5xl md:text-7xl font-black text-[#17091a] mb-8 tracking-tighter leading-tight">
            Our AI Dev <span className="bg-gradient-to-r from-[#952877] to-[#592aa7] bg-clip-text text-transparent  px-1">Process</span>
          </h2>
          <p className="text-xl text-gray-500 max-w-2xl mx-auto font-light leading-relaxed">
            A systematic, engineering-first approach to architecting robust and scalable intelligence.
          </p>
        </div>

        {/* Process Flow Visualization */}
        
        <div className="relative mb-32">
          <div className="grid md:grid-cols-3 gap-10 relative">
            {processes.map((process, index) => (
              <div key={index} className="relative group">
                <ProcessCard process={process} index={index} />
                
                {index < processes.length - 1 && (
                  <>
                    {/* Desktop Connector */}
                    <div className="hidden md:flex absolute top-1/2 -right-5 transform -translate-y-1/2 z-20 items-center opacity-20 group-hover:opacity-100 transition-opacity duration-700">
                        <div className="w-10 h-[2px] bg-gradient-to-r from-[#952877] to-[#592aa7]"></div>
                        <div className="w-2 h-2 border-t-2 border-r-2 border-[#592aa7] rotate-45 -ml-1"></div>
                    </div>

                    {/* Mobile Connector */}
                    <div className="md:hidden flex justify-center py-6 opacity-30">
                        <div className="w-[2px] h-10 bg-gradient-to-b from-[#952877] to-[#592aa7]"></div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Action Link */}
        <div className="text-center mb-32">
          <Link 
            to="/process" 
            className="group relative inline-flex items-center px-8 py-4 bg-[#17091a] text-white rounded-2xl overflow-hidden transition-all duration-500 hover:shadow-[0_20px_40px_rgba(149,40,119,0.2)]"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-[#952877] to-[#592aa7] translate-y-full group-hover:translate-y-0 transition-transform duration-500"></div>
            <span className="relative z-10 flex items-center font-black text-[10px] uppercase tracking-[0.2em]">
                Explore Methodology
                <svg className="ml-3 w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
                </svg>
            </span>
          </Link>
        </div>

        <WhoAreWe/>
        
        <div className="mt-32">
           <CTA/>
        </div>
      </div>
    </section>
  );
};

const ProcessCard = ({ process, index }) => {
  return (
    <div className="relative h-full">
      {/* Card Body with Liquid Glass effect */}
      <div className="h-full relative p-12 rounded-[3rem] bg-white/10 backdrop-blur-xl border border-zinc-700 shadow-[0_32px_64px_-16px_rgba(0,0,0,0.05)] hover:shadow-[0_48px_80px_-20px_rgba(149,40,119,0.15)] transition-all duration-700 group overflow-hidden">
        
        {/* Step Number */}
        <div className="absolute top-8 left-8 text-[10px] font-black tracking-[0.3em] text-[#952877]/80">
          PHASE / 0{index + 1}
        </div>

        {/* Icon Container */}
        <div className="relative w-24 h-24 mx-auto mb-10 bg-white rounded-[2rem] shadow-[0_12px_24px_rgba(0,0,0,0.04)] flex items-center justify-center group-hover:scale-110 group-hover:-rotate-3 transition-all duration-700">
           <img 
            src={process.iconPath} 
            alt={process.title}
            className="w-12 h-12 object-contain z-10 grayscale group-hover:grayscale-0 transition-all duration-700"
          />
          {/* Internal Glow */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#952877]/10 to-transparent rounded-[2rem] opacity-0 group-hover:opacity-100 transition-opacity"></div>
        </div>
        
        <h3 className="text-2xl font-black text-[#17091a] mb-6 tracking-tight leading-tight group-hover:text-[#952877] transition-colors duration-500">
          {process.title}
        </h3>
        <p className="text-sm font-medium text-gray-500 leading-relaxed">
          {process.description}
        </p>
        
        {/* Animated Accent */}
        <div className="absolute bottom-0 left-0 w-full h-[3px] bg-gradient-to-r from-[#952877] to-[#592aa7] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-700 origin-left"></div>
      </div>
    </div>
  );
};

export default AiDevProcess;